import { ResizeObserver } from "@juggle/resize-observer";
import { type DehydratedState, HydrationBoundary } from "@tanstack/react-query";
import type { AppProps } from "next/app";
import { Inter, Merriweather, Open_Sans } from "next/font/google";
import Head from "next/head";
import { useRouter } from "next/router";
import { ValidationError } from "yup";
import 'groupby-polyfill/lib/polyfill.js';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../styles/index.scss";
import "../styles/themes-tokens.css";
import "@mentalgrowth/ui-kit-web/dist/cjs/styles.css";
import { AnalyticsInject } from "@features/Analytics";
import { AnalyticsDebuggerProvider } from "@features/Analytics/hooks/useAnalytics";
import { FeatureFlagProvider } from "@features/FeatureToggle";
import { ThemeProvider } from "@features/Theme";
import { IntlProvider } from "@features/intl";
import { useEffect } from "react";
import { QueryClientCustomProvider } from "src/components/common/QueryClient";
import { Seo } from "src/components/common/Seo";
import { ErrorBoundary } from "src/components/common/error-boundary/error-boundary";
import { RootStoreProvider } from "src/components/common/root-store-provider/root-store-provider";
export const inter = Inter({
  weight: ["400", '500', '600', "700", "800"],
  style: ["normal", 'italic'],
  subsets: ["latin"],
  display: "swap",
  variable: "--font-inter"
});
export const merriweather = Merriweather({
  weight: ["400", "700"],
  style: ["normal", 'italic'],
  subsets: ["latin"],
  display: "swap",
  variable: "--font-merriweather"
});
export const openSans = Open_Sans({
  weight: ["400", '500', '600', "700"],
  style: ["normal", 'italic'],
  subsets: ["latin"],
  display: "swap",
  variable: "--font-open-sans"
});

// Customize yup error formatting
const formatError = ValidationError.formatError;
ValidationError.formatError = function (message, parameters) {
  if (!parameters.label) {
    parameters.label = (parameters.path + "").replace(/([a-z])([A-Z])/g, (_, match1, match2) => `${match1} ${match2.toLowerCase()}`).replace(/^[a-z]/, match => match.toUpperCase());
  }
  return formatError.call(this, message, parameters);
};

// Polyfill ResizeObserver
if (typeof window !== "undefined" && !window.ResizeObserver) {
  window.ResizeObserver = ResizeObserver;
}
const pageDescription = "Facial yoga & massage";
const keywords = "face yoga, face and neck exercises, yoga facial, facial fitness, sagging jowls, how to get rid of turkey neck, face yoga exercises, facial rejuvenation, face exercises, face massage, facial exercises for cheeks, face yoga app, non-surgical facelift, best face yoga app, neck tightening exercises, facial exercises to tighten skin, get rid of wrinkles, get rid of double chin, facial exercises for jowls, yoga for double chin, chiseled jawline, home remedies for aging skin, mimika face yoga, face exercises for sagging cheeks, dark circle treatment, facial exercises, neck sagging, best way to tighten skin on face, face lifting massage, how to remove wrinkles from face quickly, Anti-Aging skin tips, facial and massage, how to get rid of face fat, Mimika, face yoga for double chin, gua sha, face tightening exercise, remove wrinkles, facial yoga";
function MyApp({
  Component,
  pageProps
}: AppProps & {
  pageProps: {
    dehydratedState: DehydratedState;
  };
}) {
  const router = useRouter();
  const canonicalUrl = (typeof window !== "undefined" ? window.location.origin : "" + (router.asPath === "/" ? "" : router.asPath)).split("?")[0];
  useEffect(() => {
    const {
      ScCid,
      sccid
    } = router.query;
    const storedClickId = sessionStorage.getItem("ScCid");
    if (ScCid && ScCid !== storedClickId) {
      sessionStorage.setItem('ScCid', ScCid as string);
    } else if (sccid && sccid !== storedClickId) {
      sessionStorage.setItem('ScCid', sccid as string);
    }
  }, [router.query]);
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
        <meta name="viewport" content="initial-scale=1.0, width=device-width" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <meta name="description" content={pageDescription} data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <meta name="keywords" content={keywords} data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      <style jsx global>{`
        html {
          --font-inter: ${inter.style.fontFamily};
          --font-merriweather: ${merriweather.style.fontFamily};
          --font-open-sans: ${openSans.style.fontFamily};
          --font-family-base: var(--font-inter);
          --font-family-secondary: var(--font-merriweather);
        }
      `}</style>
      <Seo canonicalUrl={canonicalUrl} data-sentry-element="Seo" data-sentry-source-file="_app.tsx" />

      <QueryClientCustomProvider data-sentry-element="QueryClientCustomProvider" data-sentry-source-file="_app.tsx">
        <IntlProvider data-sentry-element="IntlProvider" data-sentry-source-file="_app.tsx">
          <ErrorBoundary data-sentry-element="ErrorBoundary" data-sentry-source-file="_app.tsx">
            <HydrationBoundary state={pageProps.dehydratedState} data-sentry-element="HydrationBoundary" data-sentry-source-file="_app.tsx">
              <RootStoreProvider data-sentry-element="RootStoreProvider" data-sentry-source-file="_app.tsx">
                <FeatureFlagProvider data-sentry-element="FeatureFlagProvider" data-sentry-source-file="_app.tsx">
                  <AnalyticsDebuggerProvider data-sentry-element="AnalyticsDebuggerProvider" data-sentry-source-file="_app.tsx">
                    <ThemeProvider data-sentry-element="ThemeProvider" data-sentry-source-file="_app.tsx">
                      <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
                      <AnalyticsInject data-sentry-element="AnalyticsInject" data-sentry-source-file="_app.tsx" />
                    </ThemeProvider>
                  </AnalyticsDebuggerProvider>
                </FeatureFlagProvider>
              </RootStoreProvider>
            </HydrationBoundary>
          </ErrorBoundary>
        </IntlProvider>
      </QueryClientCustomProvider>
    </>;
}
export default MyApp;